/**
 * 동적 레이아웃과 슬라이드 테마 랜덤 선택 유틸리티
 * DYNAMIC-LAYOUT-SPECIFICATION.md 규격에 따른 구현
 */

import { SlideTheme } from '../components/SlideEditingPanel';
import { LayoutOption, getLayoutOptionsForObjectCount } from './dynamicLayout';

// 사용 가능한 슬라이드 테마 목록
const AVAILABLE_SLIDE_THEMES: SlideTheme[] = [
  'default',
  'left-split', 
  'right-split',
  'top-split',
  'bottom-split',
  'diagonal'
];

/**
 * 랜덤 슬라이드 테마 선택
 * @returns 랜덤하게 선택된 슬라이드 테마
 */
export const getRandomSlideTheme = (): SlideTheme => {
  const randomIndex = Math.floor(Math.random() * AVAILABLE_SLIDE_THEMES.length);
  return AVAILABLE_SLIDE_THEMES[randomIndex];
};

/**
 * 콘텐츠 객체 수에 따른 랜덤 동적 레이아웃 선택
 * @param contentObjectCount 콘텐츠 객체 수 (제목 제외)
 * @returns 랜덤하게 선택된 레이아웃 옵션, 없으면 null
 */
export const getRandomDynamicLayout = (contentObjectCount: number): LayoutOption | null => {
  if (contentObjectCount === 0) {
    return null;
  }
  
  // 객체 수에 따른 적절한 레이아웃 옵션들 가져오기
  const availableLayouts = getLayoutOptionsForObjectCount(contentObjectCount);
  
  if (availableLayouts.length === 0) {
    return null;
  }
  
  // 랜덤하게 하나 선택
  const randomIndex = Math.floor(Math.random() * availableLayouts.length);
  return availableLayouts[randomIndex];
};

/**
 * 분할 위치 랜덤 생성 (슬라이드 테마에 따라)
 * @param slideTheme 적용된 슬라이드 테마
 * @returns 적절한 분할 위치 (20-80 범위)
 */
export const getRandomSplitPosition = (slideTheme: SlideTheme): number => {
  // 테마에 따라 적절한 분할 위치 범위 설정
  switch (slideTheme) {
    case 'left-split':
    case 'right-split':
      // 좌우 분할: 30-70% 범위에서 랜덤
      return Math.floor(Math.random() * 41) + 30; // 30-70
      
    case 'top-split':
    case 'bottom-split':
      // 상하 분할: 25-75% 범위에서 랜덤  
      return Math.floor(Math.random() * 51) + 25; // 25-75
      
    case 'diagonal':
      // 사선: 40-60% 범위에서 랜덤
      return Math.floor(Math.random() * 21) + 40; // 40-60
      
    case 'default':
    default:
      // 기본형: 고정값
      return 30;
  }
};

/**
 * 슬라이드별 랜덤 설정 생성
 * @param contentObjectCount 콘텐츠 객체 수
 * @returns 슬라이드에 적용할 랜덤 설정
 */
export interface RandomSlideSettings {
  slideTheme: SlideTheme;
  splitPosition: number;
  dynamicLayout: LayoutOption | null;
}

export const generateRandomSlideSettings = (contentObjectCount: number): RandomSlideSettings => {
  const slideTheme = getRandomSlideTheme();
  const splitPosition = getRandomSplitPosition(slideTheme);
  const dynamicLayout = getRandomDynamicLayout(contentObjectCount);
  
  return {
    slideTheme,
    splitPosition,
    dynamicLayout
  };
};

/**
 * 디바운스 타이머 관리 클래스
 * 분할 위치 변경 시 0.3초 디바운스 처리용
 */
export class SplitPositionDebouncer {
  private timers: Map<string, NodeJS.Timeout> = new Map();
  
  /**
   * 디바운스된 함수 실행
   * @param key 고유 키 (슬라이드 ID 등)
   * @param callback 실행할 함수
   * @param delay 지연 시간 (기본 300ms)
   */
  debounce(key: string, callback: () => void, delay = 300): void {
    // 기존 타이머가 있으면 취소
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key)!);
    }
    
    // 새 타이머 설정
    const timer = setTimeout(() => {
      callback();
      this.timers.delete(key);
    }, delay);
    
    this.timers.set(key, timer);
  }
  
  /**
   * 특정 키의 타이머 취소
   * @param key 고유 키
   */
  cancel(key: string): void {
    if (this.timers.has(key)) {
      clearTimeout(this.timers.get(key)!);
      this.timers.delete(key);
    }
  }
  
  /**
   * 모든 타이머 취소
   */
  cancelAll(): void {
    this.timers.forEach(timer => clearTimeout(timer));
    this.timers.clear();
  }
}

// 전역 디바운서 인스턴스
export const splitPositionDebouncer = new SplitPositionDebouncer();